import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Modal, Button, InputNumber, Input, Space, Spin, message,
    Tooltip, Typography, Divider,
} from 'antd';
import {
    HighlightOutlined, EditOutlined, FontSizeOutlined, DragOutlined,
    DeleteOutlined, UndoOutlined, ClearOutlined, LeftOutlined, RightOutlined,
    SaveOutlined,
} from '@ant-design/icons';
import { Stage, Layer, Line, Text as KonvaText, Image as KonvaImage, Transformer } from 'react-konva';
import Konva from 'konva';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import { useHomeAssignmentActions } from '_actions/homeAssignment.actions';

// pdf.js runs its parser in a Web Worker; the file is copied into /public by the
// client `postinstall` script (see package.json) so this same-origin path resolves.
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const { Text } = Typography;

export { MarkupEditor };

const PEN_COLORS = ['#e5484d', '#0091ff', '#30a46c']; // red / blue / green
const HIGHLIGHT_COLOR = '#ffd500';
const STAGE_WIDTH = 900; // fixed drawing-space width so annotation coords stay stable across pages/resizes
let SHAPE_SEQ = 0;
const nextId = () => `s${Date.now()}_${++SHAPE_SEQ}`;

// Fetch a Drive-served file (behind the authed photo route) as a Blob.
async function fetchBlob(fileId) {
    const res = await fetch(`/api/photos/${fileId}`, { credentials: 'include' });
    if (!res.ok) throw new Error('fetch failed');
    return res.blob();
}

function loadImageEl(url) {
    return new Promise((resolve, reject) => {
        const img = new window.Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = url;
    });
}

// Render one PDF page to an offscreen canvas at a width comfortably above STAGE_WIDTH
// (so the flattened export stays crisp). Returns { image: canvas, width, height }.
async function renderPdfPage(pdfDoc, pageNum) {
    const page = await pdfDoc.getPage(pageNum);
    const baseViewport = page.getViewport({ scale: 1 });
    const scale = (STAGE_WIDTH * 1.6) / baseViewport.width;
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const ctx = canvas.getContext('2d');
    await page.render({ canvasContext: ctx, viewport }).promise;
    return { image: canvas, width: canvas.width, height: canvas.height };
}

// Rebuild a stored shape as a Konva node — shared by the export path so the
// flattened PNG matches exactly what the teacher drew on screen.
function addShapeToLayer(layer, s) {
    if (s.type === 'text') {
        layer.add(new Konva.Text({
            x: s.x, y: s.y, text: s.text, fill: s.color,
            fontSize: s.fontSize || 22, fontStyle: 'bold',
            rotation: s.rotation || 0, scaleX: s.scaleX || 1, scaleY: s.scaleY || 1,
        }));
    } else {
        layer.add(new Konva.Line({
            points: s.points, stroke: s.color,
            strokeWidth: s.strokeWidth, opacity: s.opacity != null ? s.opacity : 1,
            lineCap: 'round', lineJoin: 'round', tension: 0.3,
            globalCompositeOperation: 'source-over',
        }));
    }
}

function dataURLtoBlob(dataURL) {
    const [meta, b64] = dataURL.split(',');
    const mime = (/:(.*?);/.exec(meta) || [])[1] || 'image/png';
    const bin = atob(b64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    return new Blob([arr], { type: mime });
}

function MarkupEditor({ visible, onClose, classId, assignment, row, onSaved }) {
    const actions = useHomeAssignmentActions();

    const [pages, setPages] = useState([]);         // flat list: { key, fileIndex, page, type, fileId, name }
    const [currentIdx, setCurrentIdx] = useState(0);
    const [bg, setBg] = useState(null);             // { image, width, height } for current page
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [tool, setTool] = useState('pen');
    const [penColor, setPenColor] = useState(PEN_COLORS[0]);
    const [annotations, setAnnotations] = useState({}); // key -> shape[]
    const [selectedId, setSelectedId] = useState(null);
    const [textEditor, setTextEditor] = useState(null); // { x, y, value, editingId }

    const [grade, setGrade] = useState(null);
    const [comment, setComment] = useState('');

    const bgCache = useRef({});   // key -> { image, width, height }
    const pdfDocs = useRef({});   // fileIndex -> pdfDoc
    const history = useRef([]);   // [{ key, shapes }] snapshots for undo
    const objectUrls = useRef([]);// revoke on close
    const isDrawing = useRef(false);
    const dirty = useRef(false);  // any annotation mutation since open → save must replace markup
    const nodeRefs = useRef({});
    const trRef = useRef(null);
    const stageRef = useRef(null);
    const canvasColRef = useRef(null);

    // Shapes live in a FIXED logical space (STAGE_WIDTH wide) so stored annotations
    // survive window resizes; the stage is then scaled by `k` to fill the container.
    // A one-shot measure reads 0 while the antd Modal open-animation is still laying
    // out (that was the "white dead strip" bug: k fell back to 1 → 900px stage in a
    // wider column). ResizeObserver re-measures as soon as the column gets real size.
    const [containerW, setContainerW] = useState(0);
    useEffect(() => {
        if (!visible) return;
        const el = canvasColRef.current;
        if (!el) return;
        const measure = () => setContainerW(el.clientWidth);
        measure();
        let ro;
        if (typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(measure);
            ro.observe(el);
        }
        window.addEventListener('resize', measure);
        return () => {
            if (ro) ro.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [visible, loading]);
    const k = containerW > 0 ? Math.max(0.3, Math.min(containerW / STAGE_WIDTH, 1.6)) : 1;

    const currentPage = pages[currentIdx] || null;
    const currentKey = currentPage ? currentPage.key : null;
    const shapes = useMemo(() => (currentKey && annotations[currentKey]) || [], [currentKey, annotations]);
    const scale = bg ? STAGE_WIDTH / bg.width : 1;
    const stageH = bg ? Math.round(bg.height * scale) : 0;

    // ── Build page list + prefill review when opened ─────────────────────────
    useEffect(() => {
        if (!visible || !row) return;
        let cancelled = false;
        setLoading(true);
        // Reload previously saved strokes (if any) so old markup is editable, not frozen.
        let loaded = {};
        try {
            const parsed = JSON.parse((row.review && row.review.annotations) || '');
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) loaded = parsed;
        } catch (e) { /* no/corrupt stored annotations → start blank */ }
        setAnnotations(loaded);
        dirty.current = false;
        setSelectedId(null);
        history.current = [];
        setCurrentIdx(0);
        setGrade(row.review && row.review.grade != null ? row.review.grade : null);
        setComment((row.review && row.review.comment) || '');

        (async () => {
            const files = row.files || [];
            const built = [];
            for (let fi = 0; fi < files.length; fi++) {
                const f = files[fi];
                const mime = f.mime_type || '';
                if (mime === 'application/pdf') {
                    try {
                        const blob = await fetchBlob(f.drive_file_id);
                        const buf = await blob.arrayBuffer();
                        const doc = await pdfjsLib.getDocument({ data: buf }).promise;
                        pdfDocs.current[fi] = doc;
                        for (let p = 1; p <= doc.numPages; p++) {
                            built.push({ key: `${fi}:${p}`, fileIndex: fi, page: p, type: 'pdf', fileId: f.drive_file_id, name: f.original_name });
                        }
                    } catch (e) { /* skip unreadable pdf */ }
                } else if (mime.startsWith('image/')) {
                    built.push({ key: `${fi}:1`, fileIndex: fi, page: 1, type: 'image', fileId: f.drive_file_id, name: f.original_name });
                }
            }
            if (!cancelled) {
                setPages(built);
                setLoading(false);
            }
        })();

        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, row]);

    // Load (and cache) the background image/PDF-page for one page entry. Shared by
    // the display effect below AND the save path — saving must be able to flatten
    // annotated pages the teacher hasn't revisited since reopening.
    const loadEntry = useCallback(async (page) => {
        const key = page.key;
        if (bgCache.current[key]) return bgCache.current[key];
        let entry;
        if (page.type === 'pdf') {
            entry = await renderPdfPage(pdfDocs.current[page.fileIndex], page.page);
        } else {
            const blob = await fetchBlob(page.fileId);
            const url = URL.createObjectURL(blob);
            objectUrls.current.push(url);
            const img = await loadImageEl(url);
            entry = { image: img, width: img.naturalWidth || img.width, height: img.naturalHeight || img.height };
        }
        bgCache.current[key] = entry;
        return entry;
    }, []);

    // ── Load background for the current page ─────────────────────────────────
    useEffect(() => {
        if (!visible || !currentPage) { setBg(null); return; }
        let cancelled = false;
        if (bgCache.current[currentPage.key]) { setBg(bgCache.current[currentPage.key]); return; }
        setBg(null);
        (async () => {
            try {
                const entry = await loadEntry(currentPage);
                if (!cancelled) setBg(entry);
            } catch (e) {
                if (!cancelled) message.error('Không tải được trang bài làm');
            }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, currentIdx, pages]);

    // Revoke object URLs when the editor closes.
    useEffect(() => {
        if (visible) return;
        objectUrls.current.forEach((u) => URL.revokeObjectURL(u));
        objectUrls.current = [];
        bgCache.current = {};
        pdfDocs.current = {};
    }, [visible]);

    // Attach transformer to a selected text node (select mode only).
    useEffect(() => {
        const tr = trRef.current;
        if (!tr) return;
        const node = selectedId ? nodeRefs.current[selectedId] : null;
        const sel = shapes.find((s) => s.id === selectedId);
        if (tool === 'select' && node && sel && sel.type === 'text') {
            tr.nodes([node]);
        } else {
            tr.nodes([]);
        }
        if (tr.getLayer()) tr.getLayer().batchDraw();
    }, [selectedId, tool, shapes]);

    const pushHistory = useCallback((key) => {
        const snap = (annotations[key] || []).map((s) => ({ ...s, points: s.points ? [...s.points] : undefined }));
        history.current.push({ key, shapes: snap });
        if (history.current.length > 100) history.current.shift();
        dirty.current = true; // every mutation snapshots first, so this flags them all
    }, [annotations]);

    const setPageShapes = useCallback((key, updater) => {
        setAnnotations((prev) => {
            const cur = prev[key] || [];
            const next = typeof updater === 'function' ? updater(cur) : updater;
            return { ...prev, [key]: next };
        });
    }, []);

    // ── Drawing handlers ─────────────────────────────────────────────────────
    // getRelativePointerPosition undoes the stage's `k` scale → logical coords.
    const getPointer = () => {
        const stage = stageRef.current;
        return stage ? stage.getRelativePointerPosition() : null;
    };

    const handleMouseDown = (e) => {
        if (!currentKey) return;
        if (tool === 'select') {
            if (e.target === e.target.getStage()) setSelectedId(null);
            return;
        }
        if (tool === 'text') {
            // Without this, the browser's default mousedown focus action fires AFTER the
            // textarea autofocuses, blurring it instantly → the note box "never appears".
            if (e.evt && e.evt.preventDefault) e.evt.preventDefault();
            const pos = getPointer();
            if (pos) setTextEditor({ x: pos.x, y: pos.y, value: '', editingId: null });
            return;
        }
        // pen / highlighter
        const pos = getPointer();
        if (!pos) return;
        isDrawing.current = true;
        pushHistory(currentKey);
        const isHi = tool === 'highlighter';
        const line = {
            id: nextId(), type: 'line',
            points: [pos.x, pos.y],
            color: isHi ? HIGHLIGHT_COLOR : penColor,
            strokeWidth: isHi ? 18 : 3,
            opacity: isHi ? 0.4 : 1,
        };
        setPageShapes(currentKey, (cur) => [...cur, line]);
    };

    const handleMouseMove = () => {
        if (!isDrawing.current || !currentKey) return;
        const pos = getPointer();
        if (!pos) return;
        setPageShapes(currentKey, (cur) => {
            if (cur.length === 0) return cur;
            const last = cur[cur.length - 1];
            const updated = { ...last, points: [...last.points, pos.x, pos.y] };
            return [...cur.slice(0, -1), updated];
        });
    };

    const handleMouseUp = () => { isDrawing.current = false; };

    const commitText = () => {
        if (!textEditor || !currentKey) { setTextEditor(null); return; }
        const value = (textEditor.value || '').trim();
        if (textEditor.editingId) {
            pushHistory(currentKey);
            setPageShapes(currentKey, (cur) => cur.map((s) =>
                s.id === textEditor.editingId ? { ...s, text: value } : s).filter((s) => s.type !== 'text' || s.text));
        } else if (value) {
            pushHistory(currentKey);
            setPageShapes(currentKey, (cur) => [...cur, {
                id: nextId(), type: 'text', x: textEditor.x, y: textEditor.y,
                text: value, color: penColor, fontSize: 22, rotation: 0, scaleX: 1, scaleY: 1,
            }]);
        }
        setTextEditor(null);
    };

    const deleteSelected = () => {
        if (!selectedId || !currentKey) return;
        pushHistory(currentKey);
        setPageShapes(currentKey, (cur) => cur.filter((s) => s.id !== selectedId));
        setSelectedId(null);
    };

    const undo = () => {
        const entry = history.current.pop();
        if (!entry) return;
        setAnnotations((prev) => ({ ...prev, [entry.key]: entry.shapes }));
        setSelectedId(null);
    };

    const clearPage = () => {
        if (!currentKey) return;
        pushHistory(currentKey);
        setPageShapes(currentKey, []);
        setSelectedId(null);
    };

    // Del key deletes the selected shape.
    useEffect(() => {
        if (!visible) return;
        const onKey = (e) => {
            if ((e.key === 'Delete' || e.key === 'Backspace') && selectedId && !textEditor) {
                e.preventDefault();
                deleteSelected();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, selectedId, textEditor, currentKey]);

    // ── Save ─────────────────────────────────────────────────────────────────
    const handleSave = async () => {
        const studentId = row.student_id;
        // Collect every annotated page → flatten to PNG via an offscreen Konva stage.
        const annotatedKeys = Object.keys(annotations).filter((k) => (annotations[k] || []).length > 0);
        setSaving(true);
        try {
            const files = [];
            const meta = [];
            for (const key of annotatedKeys) {
                const page = pages.find((p) => p.key === key);
                if (!page) continue;
                const entry = await loadEntry(page); // may not be cached if page wasn't revisited
                const w = STAGE_WIDTH;
                const h = Math.round(entry.height * (STAGE_WIDTH / entry.width));
                const container = document.createElement('div');
                const stage = new Konva.Stage({ container, width: w, height: h });
                const layer = new Konva.Layer();
                stage.add(layer);
                layer.add(new Konva.Image({ image: entry.image, x: 0, y: 0, width: w, height: h }));
                for (const s of (annotations[key] || [])) addShapeToLayer(layer, s);
                layer.draw();
                const pixelRatio = Math.max(1, Math.min(2, 1600 / w));
                const dataURL = stage.toDataURL({ pixelRatio, mimeType: 'image/png' });
                stage.destroy();
                const [fi, pg] = key.split(':');
                files.push(new File([dataURLtoBlob(dataURL)], `${studentId}-p${pg}.png`, { type: 'image/png' }));
                meta.push({ source_index: Number(fi), page: Number(pg) });
            }

            // Replace stored markup when there are flattened pages OR the teacher touched
            // annotations (incl. clearing everything — that's how markup gets deleted).
            // Untouched grade/comment-only saves leave existing marked copies alone.
            const replaceFiles = files.length > 0 || dirty.current;
            const annotationsJSON = JSON.stringify(
                annotatedKeys.reduce((acc, kk) => { acc[kk] = annotations[kk]; return acc; }, {})
            );
            if (replaceFiles && annotationsJSON.length > 900 * 1024) {
                message.error('Nét vẽ quá nhiều, không thể lưu — hãy xóa bớt');
                setSaving(false);
                return;
            }

            const res = await actions.saveReview(classId, assignment._id || assignment.id, studentId, {
                grade, comment,
                files, meta,
                replaceFiles,
                annotations: annotationsJSON,
            });
            if (res && res.ok) {
                message.success('Đã lưu bài chấm');
                if (onSaved) onSaved();
                onClose();
            } else {
                message.error((res && res.message) || 'Lưu bài chấm thất bại');
            }
        } catch (e) {
            message.error('Lưu bài chấm thất bại');
        }
        setSaving(false);
    };

    const existingMarked = (row && row.review && row.review.marked_files) || [];

    const toolBtn = (key, icon, label) => (
        <Tooltip title={label} key={key}>
            <Button
                type={tool === key ? 'primary' : 'default'}
                icon={icon}
                onClick={() => { setTool(key); setSelectedId(null); }}
            />
        </Tooltip>
    );

    return (
        <Modal
            title={`Chấm bài — ${row ? row.name : ''}${row && row.student_id ? ` (${row.student_id})` : ''}`}
            visible={visible}
            onCancel={onClose}
            footer={null}
            width="96vw"
            style={{ top: 12, maxWidth: 1400 }}
            bodyStyle={{ padding: 12 }}
            destroyOnClose
        >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                <Space size={4}>
                    {toolBtn('pen', <EditOutlined />, 'Bút (vẽ tự do)')}
                    {toolBtn('highlighter', <HighlightOutlined />, 'Bút dạ quang')}
                    {toolBtn('text', <FontSizeOutlined />, 'Ghi chú chữ')}
                    {toolBtn('select', <DragOutlined />, 'Chọn / di chuyển')}
                </Space>
                <Divider type="vertical" />
                <Space size={6}>
                    {PEN_COLORS.map((c) => (
                        <span
                            key={c}
                            onClick={() => setPenColor(c)}
                            title="Màu bút"
                            style={{
                                width: 22, height: 22, borderRadius: '50%', background: c,
                                cursor: 'pointer', display: 'inline-block',
                                border: penColor === c ? '3px solid #333' : '2px solid #fff',
                                boxShadow: '0 0 0 1px #ccc',
                            }}
                        />
                    ))}
                </Space>
                <Divider type="vertical" />
                <Space size={4}>
                    <Tooltip title="Hoàn tác"><Button icon={<UndoOutlined />} onClick={undo} /></Tooltip>
                    <Tooltip title="Xóa nét đã chọn"><Button icon={<DeleteOutlined />} onClick={deleteSelected} disabled={!selectedId} /></Tooltip>
                    <Tooltip title="Xóa hết trang này"><Button icon={<ClearOutlined />} onClick={clearPage} danger /></Tooltip>
                </Space>
            </div>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {/* Canvas column */}
                <div ref={canvasColRef} style={{ flex: '1 1 620px', minWidth: 320 }}>
                    {pages.length > 1 && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 6 }}>
                            <Button size="small" icon={<LeftOutlined />} disabled={currentIdx === 0}
                                onClick={() => { setSelectedId(null); setCurrentIdx((i) => Math.max(0, i - 1)); }} />
                            <Text type="secondary">
                                Trang {currentIdx + 1}/{pages.length}
                                {currentPage ? ` — ${currentPage.name || ''}${currentPage.type === 'pdf' ? ` (tr.${currentPage.page})` : ''}` : ''}
                            </Text>
                            <Button size="small" icon={<RightOutlined />} disabled={currentIdx >= pages.length - 1}
                                onClick={() => { setSelectedId(null); setCurrentIdx((i) => Math.min(pages.length - 1, i + 1)); }} />
                        </div>
                    )}

                    {/* fit-content: the box hugs the stage, so a mis-measured k can never
                        leave a dead (undrawable) strip between stage edge and box edge. */}
                    <div style={{ position: 'relative', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'auto', background: '#fafafa', maxHeight: '72vh', width: 'fit-content', maxWidth: '100%', margin: '0 auto' }}>
                        {(loading || !bg) ? (
                            <div style={{ textAlign: 'center', padding: 60 }}>
                                <Spin /> <div style={{ marginTop: 8 }}><Text type="secondary">Đang tải bài làm…</Text></div>
                            </div>
                        ) : (
                            <>
                                <Stage
                                    ref={stageRef}
                                    width={Math.round(STAGE_WIDTH * k)}
                                    height={Math.round(stageH * k)}
                                    scaleX={k}
                                    scaleY={k}
                                    onMouseDown={handleMouseDown}
                                    onMousemove={handleMouseMove}
                                    onMouseup={handleMouseUp}
                                    onTouchStart={handleMouseDown}
                                    onTouchMove={handleMouseMove}
                                    onTouchEnd={handleMouseUp}
                                    style={{ background: '#fff' }}
                                >
                                    <Layer listening={false}>
                                        <KonvaImage image={bg.image} width={STAGE_WIDTH} height={stageH} />
                                    </Layer>
                                    <Layer>
                                        {shapes.map((s) => s.type === 'text' ? (
                                            <KonvaText
                                                key={s.id}
                                                ref={(n) => { if (n) nodeRefs.current[s.id] = n; }}
                                                text={s.text}
                                                x={s.x}
                                                y={s.y}
                                                fill={s.color}
                                                fontSize={s.fontSize || 22}
                                                fontStyle="bold"
                                                rotation={s.rotation || 0}
                                                scaleX={s.scaleX || 1}
                                                scaleY={s.scaleY || 1}
                                                draggable={tool === 'select'}
                                                onClick={() => tool === 'select' && setSelectedId(s.id)}
                                                onTap={() => tool === 'select' && setSelectedId(s.id)}
                                                onDblClick={() => setTextEditor({ x: s.x, y: s.y, value: s.text, editingId: s.id })}
                                                onDragStart={() => { pushHistory(currentKey); }}
                                                onDragEnd={(e) => setPageShapes(currentKey, (cur) => cur.map((sh) => sh.id === s.id ? { ...sh, x: e.target.x(), y: e.target.y() } : sh))}
                                                onTransformEnd={(e) => {
                                                    const node = e.target;
                                                    setPageShapes(currentKey, (cur) => cur.map((sh) => sh.id === s.id ? {
                                                        ...sh, x: node.x(), y: node.y(),
                                                        rotation: node.rotation(), scaleX: node.scaleX(), scaleY: node.scaleY(),
                                                    } : sh));
                                                }}
                                            />
                                        ) : (
                                            <Line
                                                key={s.id}
                                                points={s.points}
                                                stroke={s.color}
                                                strokeWidth={s.strokeWidth}
                                                opacity={s.opacity != null ? s.opacity : 1}
                                                lineCap="round"
                                                lineJoin="round"
                                                tension={0.3}
                                                hitStrokeWidth={16}
                                                onClick={() => tool === 'select' && setSelectedId(s.id)}
                                                onTap={() => tool === 'select' && setSelectedId(s.id)}
                                            />
                                        ))}
                                        <Transformer ref={trRef} rotateEnabled enabledAnchors={['top-left', 'top-right', 'bottom-left', 'bottom-right']} />
                                    </Layer>
                                </Stage>

                                {textEditor && (
                                    <div style={{ position: 'absolute', left: Math.min(textEditor.x * k, STAGE_WIDTH * k - 220), top: textEditor.y * k, zIndex: 10 }}>
                                        <Input.TextArea
                                            autoFocus
                                            autoSize={{ minRows: 1, maxRows: 4 }}
                                            value={textEditor.value}
                                            placeholder="Nhập ghi chú, Enter để lưu"
                                            onChange={(e) => setTextEditor({ ...textEditor, value: e.target.value })}
                                            onPressEnter={(e) => { e.preventDefault(); commitText(); }}
                                            onBlur={commitText}
                                            style={{ width: 220 }}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Legacy strip: markup saved BEFORE stroke-data storage existed can't be
                        re-edited (only the flattened PNG survives). New saves reload straight
                        into the canvas above, so the strip disappears for them. */}
                    {existingMarked.length > 0 && !(row && row.review && row.review.annotations) && (
                        <div style={{ marginTop: 10 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>Bài đã chấm trước đó (bản cũ — sẽ bị thay khi lưu lại nét vẽ):</Text>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                                {existingMarked.map((m) => (
                                    <a key={m.drive_file_id} href={`/api/photos/${m.drive_file_id}`} target="_blank" rel="noopener noreferrer">
                                        <img
                                            src={`/api/photos/${m.drive_file_id}`}
                                            alt="Bài đã chấm"
                                            style={{ width: 70, height: 70, objectFit: 'cover', border: '1px solid #ddd', borderRadius: 4 }}
                                        />
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Grade panel */}
                <div style={{ flex: '0 0 240px', minWidth: 220 }}>
                    <div style={{ marginBottom: 10 }}>
                        <div style={{ marginBottom: 4 }}><Text strong>Điểm (0–10)</Text></div>
                        <InputNumber
                            min={0} max={10} step={0.25} value={grade}
                            onChange={(v) => setGrade(v)}
                            style={{ width: '100%' }}
                            placeholder="Chưa chấm"
                        />
                    </div>
                    <div style={{ marginBottom: 10 }}>
                        <div style={{ marginBottom: 4 }}><Text strong>Nhận xét</Text></div>
                        <Input.TextArea
                            rows={5} value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="Nhận xét cho học sinh…"
                        />
                    </div>
                    <Button type="primary" icon={<SaveOutlined />} block loading={saving} onClick={handleSave}>
                        Lưu
                    </Button>
                    {pages.length === 0 && !loading && (
                        <div style={{ marginTop: 8 }}><Text type="secondary" style={{ fontSize: 12 }}>Không có tệp ảnh/PDF để chấm; bạn vẫn có thể lưu điểm và nhận xét.</Text></div>
                    )}
                </div>
            </div>
        </Modal>
    );
}
